/**
 * User-facing copy for the end-user screens (spec §9). Whole sentences only;
 * placeholders are function parameters so French can reorder them later.
 * No runtime i18n library yet: this module is the seam.
 */
export const strings = {
  outcome: {
    answered: 'Answered',
    missed: 'Missed',
    noAnswer: 'No answer',
    unknown: 'Unknown',
  },
  quality: {
    healthy: 'Healthy',
    degraded: 'Degraded',
    poor: 'Poor',
    unknown: 'Unknown',
    unknownReason: 'Not enough evidence to assess this call.',
  },
  myCalls: {
    routedNoDetail: 'This call was routed to you by an auto attendant or queue and has no carrier record to open.',
    title: 'My Calls',
    tiles: {
      conversations: 'Conversations',
      conversationsHint: 'Calls that connected (outbound attempts included)',
      missed: 'Missed',
      missedHint: 'Inbound calls you did not answer',
      missedHintQueueCaveat: 'Inbound calls you did not answer. Queue calls answered by a colleague are not counted.',
      people: 'People',
      peopleHint: 'Distinct people in this period',
      talkTime: 'Talk time',
      average: 'Average',
      inbound: 'Inbound',
      outbound: 'Outbound',
      viaQueue: 'Via queue',
    },
    peopleCard: 'People I talk to most',
    peopleCardHint: 'Who appeared most often in your calls',
    peopleEmpty: 'No one appears in this period yet.',
    recentCard: 'Recent interactions',
    recentCardHint: 'Your latest conversations',
    recentEmpty: 'No interactions in this period.',
    viewFullHistory: 'View full call history',
    trendCard: 'Activity trend',
    trendCardHint: 'Inbound and outbound activity across the selected period',
    howCounted: 'How this was counted',
    directCalls: 'Direct',
    routedCalls: 'Routed through a queue or attendant',
    routingUnavailable: 'Routing details are temporarily unavailable',
    routedOverlap: (n: number) => `${n} routed calls still need matching before they can be included.`,
    durationFootnote: 'Durations come from the call record; Teams timing is used when the record has none.',
    callsWith: (name: string) => `Calls with ${name}`,
    lastSpoke: (when: string) => `Last: ${when}`,
    inOut: (inbound: number, outbound: number) => `In ${inbound} / Out ${outbound}`,
    calls: (n: number) => (n === 1 ? '1 call' : `${n} calls`),
  },
  interaction: {
    breadcrumb: 'Interactions',
    title: 'Interaction',
    back: 'Back',
    with: (party: string) => `With ${party}`,
    direction: 'Direction',
    started: 'Started',
    directionInbound: 'Inbound',
    directionOutbound: 'Outbound',
    journeyCard: 'Journey',
    journeyHint: 'How this call reached you',
    journeyUnavailable: 'The route for this call is not available.',
    qualityCard: 'Call quality',
    evidence: 'Technical evidence',
    reportProblem: 'Report a problem',
    reported: 'Reported',
    reportedRef: (ref: string) => `Reported · ${ref}`,
    you: 'You',
    mainLine: 'Main line',
    autoAttendant: (name: string | null) => (name ? `Auto attendant · ${name}` : 'Auto attendant'),
    callQueue: (name: string | null) => (name ? `Queue · ${name}` : 'Queue'),
    carrier: (name: string | null) => (name ? `Carrier · ${name}` : 'Carrier'),
    sbc: 'SBC',
    teams: 'Teams',
    voicemail: 'Voicemail',
    // m:ss under each journey segment (spec §6): "0:41", "3:24" -- never "0:00".
    segmentDuration: (m: number, s: number) => `${m}:${String(s).padStart(2, '0')}`,
    outcomeAtEnd: (word: string) => word,
  },
  narrative: {
    inboundStart: (from: string) => `${from} called`,
    inboundStartOnMainLine: (from: string) => `${from} called on the main line`,
    outboundStart: (to: string) => `You called ${to}`,
    viaMainLine: 'on the main line',
    // Owner send-back F5d: never name infrastructure (SBC/carrier/Teams/
    // signal group); name the product-level hop only (attendant, queue).
    reachedAttendant: (name: string | null) => (name ? `reached the ${name} attendant` : 'reached the attendant'),
    waitedInQueue: (name: string | null, duration: string | null) => {
      const base = name ? `waited in the ${name} queue` : 'waited in the queue';
      return duration ? `${base} ${duration}` : base;
    },
    rangYou: 'it rang you',
    rangPerson: (name: string) => `it rang ${name}`,
    answeredByWithDuration: (name: string, duration: string | null) => (duration ? `was answered by ${name} (${duration})` : `was answered by ${name}`),
    youAnsweredWithDuration: (duration: string | null) => (duration ? `you answered (${duration})` : 'you answered'),
    transferredTo: (name: string) => `transferred to ${name}`,
    wentToVoicemail: 'it went to voicemail',
    notAnswered: 'it was not answered',
    ended: (duration: string) => `ended after ${duration}`,
    // Unknown durations never render "0:00" (JourneyFlow's fmt() already
    // returns an empty string in that case) -- a distinct whole sentence,
    // not `ended` with a fabricated parenthetical.
    endedNoDuration: 'it ended',
    unknownRoute: 'The route for this call is not known.',
  },
  report: {
    dialogTitle: 'Report a problem with this call',
    category: 'What went wrong?',
    categories: {
      audio_quality: 'Audio or call quality',
      routing_transfer: 'Routing or transfer',
      caller_id: 'Caller ID or number',
      customer_mapping: 'Customer or contact',
      other: 'Something else',
    },
    description: 'Describe what happened',
    descriptionPlaceholder: 'What did you hear or see, and when?',
    submit: 'Send report',
    cancel: 'Cancel',
    close: 'Done',
    submitted: (ref: string) => `Report ${ref} sent to your support team.`,
    deliveryFailed: (ref: string) => `Report ${ref} was saved but could not be emailed yet. Your support team can still see it.`,
    duplicateTitle: 'You already reported this',
    duplicateBody: (ref: string) => `Report ${ref} is already open for this call and category.`,
    reportAnother: 'Report another issue',
    missingTitle: 'Report a missing call',
    missingIntro: 'Tell us about a call you expected but cannot find here.',
    missingWhen: 'Approximate date and time',
    missingDirection: 'Direction',
    missingNumber: 'Phone number, if known',
    missingExpectedUser: 'Which person should have received it, if known',
    missingExpectedQueue: 'Which queue or auto attendant, if known',
    missingExpectedBranch: 'Which site or branch, if known',
    missingSubmitted: (ref: string) => `Missing-call report ${ref} sent.`,
    directionInbound: 'Inbound',
    directionOutbound: 'Outbound',
    directionUnknown: 'Not sure',
  },
  callHistory: {
    outcome: 'Outcome',
    reportMissing: 'Report missing call',
    durationFootnote: 'Durations come from the call record; Teams timing is used when the record has none.',
  },
  settings: {
    supportCard: 'Support reports',
    supportCardHint: 'Where "Report a problem" emails are delivered. Use your helpdesk ingest address.',
    ingestAddress: 'Support email address',
    save: 'Save',
    sendTest: 'Send test email',
    saved: 'Saved.',
    testSent: (to: string) => `Test email sent to ${to}.`,
    testFailed: (reason: string) => `Test email failed: ${reason}`,
    notConfigured: 'Not configured. Reports are saved but not emailed until an address is set.',
  },
} as const;

export type Strings = typeof strings;
